import { useState, useCallback } from 'react';
import { API_BASE_URL } from '../config';

export interface FlowStep {
  id: string;
  question: string;
  type: string;
  required: boolean;
  options?: string[];
}

export interface FlowState {
  sessionId: string | null;
  flowId: string | null;
  status: 'idle' | 'active' | 'completed' | 'executing' | 'error';
  currentStep: FlowStep | null;
  allSteps: FlowStep[] | null;
  collectedData: Record<string, any>;
  errorMessage?: string;
  result?: any;
}

export function useFlowEngine() {
  const [state, setState] = useState<FlowState>({
    sessionId: null,
    flowId: null,
    status: 'idle',
    currentStep: null,
    allSteps: null,
    collectedData: {},
  });

  const startFlow = useCallback(async (flowId: string, initialData: Record<string, any> = {}, token: string) => {
    setState(prev => ({ ...prev, status: 'active', errorMessage: undefined }));
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/flows/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ flow_id: flowId, initial_data: initialData }),
      });

      if (!response.ok) throw new Error('Failed to start flow');

      const data = await response.json();
      setState({
        sessionId: data.session_id,
        flowId: flowId,
        status: data.status,
        currentStep: data.current_step,
        allSteps: data.all_steps,
        collectedData: data.collected_data,
      });
      return data;
    } catch (error: any) {
      setState(prev => ({ ...prev, status: 'error', errorMessage: error.message }));
      throw error;
    }
  }, []);

  const nextStep = useCallback(async (answer: any, token: string) => {
    if (!state.sessionId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/flows/next`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ session_id: state.sessionId, answer }),
      });

      if (!response.ok) throw new Error('Failed to advance flow');

      const data = await response.json();
      setState(prev => ({
        ...prev,
        status: data.status,
        currentStep: data.current_step,
        allSteps: data.all_steps,
        collectedData: data.collected_data,
      }));
      return data;
    } catch (error: any) {
      setState(prev => ({ ...prev, status: 'error', errorMessage: error.message }));
      throw error;
    }
  }, [state.sessionId]);

  const bulkNext = useCallback(async (answers: Record<string, any>, token: string) => {
    if (!state.sessionId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/flows/next`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ session_id: state.sessionId, answers }),
      });

      if (!response.ok) throw new Error('Failed to submit bulk answers');

      const data = await response.json();
      setState(prev => ({
        ...prev,
        status: data.status,
        currentStep: data.current_step,
        allSteps: data.all_steps,
        collectedData: data.collected_data,
      }));
      return data;
    } catch (error: any) {
      setState(prev => ({ ...prev, status: 'error', errorMessage: error.message }));
      throw error;
    }
  }, [state.sessionId]);

  const executeFlow = useCallback(async (token: string) => {
    if (!state.sessionId) return;

    setState(prev => ({ ...prev, status: 'executing' }));
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/flows/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ session_id: state.sessionId }),
      });

      if (!response.ok) throw new Error('Failed to execute flow agent');

      const data = await response.json();
      setState(prev => ({
        ...prev,
        status: 'completed',
        result: data,
      }));
      return data;
    } catch (error: any) {
      setState(prev => ({ ...prev, status: 'error', errorMessage: error.message }));
      throw error;
    }
  }, [state.sessionId]);

  const resetFlow = useCallback(() => {
    setState({
      sessionId: null,
      flowId: null,
      status: 'idle',
      currentStep: null,
      allSteps: null,
      collectedData: {},
    });
  }, []);

  return {
    ...state,
    startFlow,
    nextStep,
    bulkNext,
    executeFlow,
    resetFlow
  };
}
